import React, { useState, useEffect, useMemo, useRef } from 'react';
import { InformePericial, ElementoValoracion, FaseInforme } from '../../types';
import { informeService } from '../../services/api';
import { showApiError, showSuccessToast } from '../../utils/toast';
import ElementoValoracionModal from './ElementoValoracionModal';
import { 
    FiPlus, 
    FiEdit2, 
    FiTrash2, 
    FiDownload, 
    FiUpload, 
    FiChevronUp, 
    FiChevronDown, 
    FiArrowRight 
} from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import './ValoracionFase.css';

interface Props {
    informe?: InformePericial;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

type SortColumn = 'tipo' | 'perjudicado' | 'ia' | 'concepto' | 'unidades' | 'precio_unitario' | 'precio_total' | 'depreciacion' | 'valor_real';
type SortOrder = 'asc' | 'desc';

const TERCERO_COLOR_CLASSES = ['badge-purple', 'badge-indigo', 'badge-pink', 'badge-teal', 'badge-orange'];

const getTerceroBadgeClass = (nombre?: string): string => {
    if (!nombre || !nombre.trim()) return 'badge-purple';
    const clean = nombre.trim().toLowerCase();
    let hash = 0;
    for (let i = 0; i < clean.length; i++) {
        hash = clean.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % TERCERO_COLOR_CLASSES.length;
    return TERCERO_COLOR_CLASSES[index];
};

const ValoracionFase: React.FC<Props> = ({ informe, onUpdate }) => {
    const [items, setItems] = useState<ElementoValoracion[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingItem, setEditingItem] = useState<ElementoValoracion | null>(null);
    const [sortColumn, setSortColumn] = useState<SortColumn>('concepto');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (informe?.id) {
            prvCargarTabla();
        }
    }, [informe?.id]);

    const prvCargarTabla = async () => {
        if (!informe) return;
        try {
            setLoading(true);
            const res = await informeService.obtenerTablaValoracion(informe.id);
            setItems(res.data.items || []);
        } catch (err: any) {
            console.error("Error al cargar valoración:", err);
            // Fallback to informe.tabla_valoracion if available
            setItems((informe.tabla_valoracion as any) || []);
        } finally {
            setLoading(false);
        }
    };

    const prvGuardarTablaBackend = async (newItems: ElementoValoracion[]) => {
        if (!informe) return;
        try {
            const res = await informeService.guardarTablaValoracion(informe.id, newItems);
            setItems(res.data.items || newItems);
            onUpdate({ ...informe, tabla_valoracion: res.data.items as any });
            showSuccessToast("Tabla de valoración guardada");
        } catch (err) {
            showApiError(err);
        }
    };

    const handleAddItem = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    const handleEditItem = (item: ElementoValoracion) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleDeleteItem = async (id?: string) => {
        if (!id) return;
        if (window.confirm("¿Estás seguro de que deseas eliminar este elemento?")) {
            const nextItems = items.filter(it => it.id !== id);
            await prvGuardarTablaBackend(nextItems);
        }
    };

    const handleSaveModal = async (savedItem: ElementoValoracion) => {
        let nextItems: ElementoValoracion[];
        const exists = items.some(it => it.id === savedItem.id);
        if (exists) {
            nextItems = items.map(it => it.id === savedItem.id ? savedItem : it);
        } else {
            nextItems = [...items, savedItem];
        }
        await prvGuardarTablaBackend(nextItems);
    };

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            let valA: any = a[sortColumn];
            let valB: any = b[sortColumn];

            if (typeof valA === 'string') valA = valA.toLowerCase();
            if (typeof valB === 'string') valB = valB.toLowerCase();

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [items, sortColumn, sortOrder]);

    const totalPrecioTotal = useMemo(() => {
        return items.reduce((acc, curr) => acc + (Number(curr.precio_total) || 0), 0);
    }, [items]);

    const totalValorReal = useMemo(() => {
        return items.reduce((acc, curr) => acc + (Number(curr.valor_real) || 0), 0);
    }, [items]);

    const handleExportExcel = async () => {
        if (!informe) return;
        try {
            const response = await informeService.descargarValoracionExcel(informe.id);
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safeExp = (informe.numero_expediente || 'informe').replace(/[^a-zA-Z0-9_-]/g, '');
            a.download = `${safeExp}_valoracion.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showSuccessToast("Excel exportado correctamente");
        } catch (err) {
            showApiError(err);
        }
    };

    const handleImportExcelClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !informe) return;

        try {
            setLoading(true);
            const res = await informeService.importarValoracionExcel(informe.id, file);
            setItems(res.data.items || []);
            onUpdate({ ...informe, tabla_valoracion: res.data.items as any });
            showSuccessToast(res.data.mensaje || "Excel importado correctamente");
        } catch (err) {
            showApiError(err);
        } finally {
            setLoading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleSiguienteFase = async () => {
        if (!informe) return;
        try {
            const res = await informeService.cambiarFase(informe.id, 'informe');
            onUpdate(res.data, 'informe');
            showSuccessToast("Avanzando a la fase de Informe IA");
        } catch (err) {
            showApiError(err);
        }
    };

    const renderSortHeader = (column: SortColumn, label: string, align: 'left' | 'center' | 'right' = 'left') => {
        const isActive = sortColumn === column;
        return (
            <th 
                onClick={() => handleSort(column)}
                style={{ textAlign: align }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start' }}>
                    <span>{label}</span>
                    {isActive && (
                        sortOrder === 'asc' ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />
                    )}
                </div>
            </th>
        );
    };

    return (
        <div className="valoracion-fase">
            {/* Header section */}
            <div className="valoracion-header">
                <div>
                    <h2 className="valoracion-title">
                        <FaEuroSign className="valoracion-title-icon" />
                        Valoración del Siniestro
                    </h2>
                </div>
                <button
                    onClick={handleSiguienteFase}
                    className="btn-primary"
                >
                    <span>Siguiente Fase: Informe IA</span>
                    <FiArrowRight />
                </button>
            </div>

            {/* Action Bar */}
            <div className="valoracion-toolbar">
                <button
                    onClick={handleAddItem}
                    className="btn-primary"
                >
                    <FiPlus />
                    <span>Añadir Elemento</span>
                </button>

                <div className="valoracion-actions-group">
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange}
                        accept=".xlsx, .xls" 
                        style={{ display: 'none' }} 
                    />
                    <button
                        onClick={handleImportExcelClick}
                        className="btn-secondary"
                        title="Importar tabla desde un archivo Excel"
                    >
                        <FiUpload />
                        <span>Importar Excel</span>
                    </button>
                    <button
                        onClick={handleExportExcel}
                        className="btn-success"
                        title="Exportar tabla a archivo Excel (.xlsx)"
                    >
                        <FiDownload />
                        <span>Exportar Excel</span>
                    </button>
                </div>
            </div>

            {/* Interactive Table / Clean Empty State */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>Cargando elementos de valoración...</div>
            ) : items.length === 0 ? (
                <div className="valoracion-empty-container">
                    <button
                        onClick={handleAddItem}
                        className="btn-primary"
                    >
                        <FiPlus />
                        <span>Añadir Primer Elemento</span>
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="valoracion-table">
                        <thead>
                            <tr>
                                {renderSortHeader('tipo', 'Tipo', 'left')}
                                {renderSortHeader('perjudicado', 'Perjudicado', 'center')}
                                {renderSortHeader('ia', 'I/A', 'center')}
                                {renderSortHeader('concepto', 'Concepto', 'left')}
                                {renderSortHeader('unidades', 'Unidades', 'right')}
                                {renderSortHeader('precio_unitario', 'Precio Unit.', 'right')}
                                {renderSortHeader('precio_total', 'Precio Total', 'right')}
                                {renderSortHeader('depreciacion', 'Deprec. (%)', 'right')}
                                {renderSortHeader('valor_real', 'Valor Real', 'right')}
                                <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map((item) => (
                                <tr key={item.id}>
                                    <td style={{ fontWeight: 600 }}>{item.tipo}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {item.perjudicado === 'Tercero' ? (
                                            <span 
                                                className={`badge ${getTerceroBadgeClass(item.nombre_tercero)}`}
                                                title={item.nombre_tercero ? `Tercero: ${item.nombre_tercero}` : 'Tercero'}
                                            >
                                                Tercero
                                            </span>
                                        ) : (
                                            <span className="badge badge-blue">
                                                Asegurado
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <span className={`badge ${item.ia === 'Asistencia' ? 'badge-amber' : 'badge-emerald'}`}>
                                            {item.ia}
                                        </span>
                                    </td>
                                    <td title={item.concepto}>{item.concepto}</td>
                                    <td style={{ textAlign: 'right' }}>{item.unidades}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {Number(item.precio_unitario).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                        {Number(item.precio_total).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {Number(item.depreciacion)}%
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>
                                        {Number(item.valor_real).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                            <button
                                                onClick={() => handleEditItem(item)}
                                                className="icon-action-btn"
                                                title="Editar"
                                            >
                                                <FiEdit2 size={15} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteItem(item.id)}
                                                className="icon-action-btn delete"
                                                title="Eliminar"
                                            >
                                                <FiTrash2 size={15} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={6} style={{ textAlign: 'right', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b' }}>
                                    TOTALES ({items.length} elementos):
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                    {totalPrecioTotal.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                </td>
                                <td></td>
                                <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1rem', color: '#15803d' }}>
                                    {totalValorReal.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Edit/Create Modal */}
            <ElementoValoracionModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveModal}
                initialData={editingItem}
            />
        </div>
    );
};

export default ValoracionFase;
